import React, { createContext, useContext, useState, useEffect } from "react";
import AuthService, { PENDING_OTP_KEY } from "../../Services/auth/AuthService";
import { normalizeUserRole } from "../../Layout/Sidebar/Menu";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [authenticated, setAuthenticated] = useState(AuthService.isAuthenticated());
    const [userData, setUserData] = useState({});
    const [loading, setLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(() => AuthService.hasPendingOtp());

    const login = async (email, password) => {
        const { message, success } = await AuthService.login(email, password);
        if (success) {
            AuthService.beginOtpFlow(email);
            setIsLoggedIn(true);
            setAuthenticated(false);
            setUserData({});
        } else {
            setIsLoggedIn(false);
            console.error(message);
        }
        return { message, success };
    };

    const clearOtpFlow = () => {
        sessionStorage.removeItem(PENDING_OTP_KEY);
        setIsLoggedIn(false);
    };

    const loginOtp = async (otp, email) => {
        setLoading(true);
        const { message, success } = await AuthService.loginOtp(email, otp);
        if (success) {
            // Keep OTP pending flag until /me succeeds so the OTP route guard
            // does not bounce to /login mid-flight (isLoggedIn cleared early).
            await getUser();
            setIsLoggedIn(false);
            setAuthenticated(true);
        } else {
            console.error(message);
        }
        setLoading(false);
        return { message, success };
    };

    const getUser = async () => {
        if (AuthService.hasPendingOtp()) {
            setAuthenticated(false);
            setLoading(false);
            return;
        }
        if (!AuthService.isAuthenticated()) {
            setAuthenticated(false);
            setLoading(false);
            return;
        }
        const { success, user, role, error } = await AuthService.getUser();
        if (success) {
            const finalRole = normalizeUserRole(role || user?.role);
            setUserData({ ...user, role: finalRole });
            setAuthenticated(true);
        } else {
            console.error("Failed to fetch user data", error);
            await AuthService.logout({ revokeOnServer: false });
            setAuthenticated(false);
            setUserData({});
        }
        setLoading(false);
    };

    useEffect(() => {
        getUser();
    }, []);

    const logout = async () => {
        await AuthService.logout();
        setIsLoggedIn(false);
        setAuthenticated(false);
        setUserData({});
        localStorage.removeItem("color1");
        localStorage.removeItem("color2");
        document.documentElement.style.setProperty("--theme-default", '#D51515');
        document.documentElement.style.setProperty("--theme-secondary", '#1d1a1b');
    };

    return (
        <AuthContext.Provider
            value={{
                authenticated,
                login,
                loginOtp,
                clearOtpFlow,
                isLoggedIn,
                logout,
                setAuthenticated,
                getUser,
                userData,
                loading,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};
